import {
  mergeCallbackOptions,
  mergeCallbackOptionsWithPick,
} from "@captions-web/utils/merge-callbacks";
import { omitKeys } from "@captions-web/utils/pick-by";
import { DefaultError, useMutation, UseMutationOptions } from "@tanstack/react-query";
import { z } from "zod";

import { makeDefaultMutationFetcher } from "../../default-fetcher";
import {
  BaseRequestCompletionContext,
  CallerCombinedVariables,
  MutationDefinitionOptions,
  MutationFetcherOptions,
} from "../types";

type MutationTriggerOptions<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  TransformedResult,
> = MutationFetcherOptions<Path, ParamsSchema, TransformedResult> &
  CallerCombinedVariables<Path, ParamsSchema>;

interface UseMutationHookCallerOptions<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  TransformedResult,
> extends MutationFetcherOptions<Path, ParamsSchema, TransformedResult>,
    Omit<
      UseMutationOptions<
        TransformedResult,
        DefaultError,
        CallerCombinedVariables<Path, ParamsSchema>,
        unknown
      >,
      "mutationFn" | "onSuccess" | "onError"
    > {
  onSuccess?: (
    result: TransformedResult,
    variables: CallerCombinedVariables<Path, ParamsSchema>,
    context: BaseRequestCompletionContext
  ) => Promise<void> | void;
  onError?: (
    error: Error,
    variables: CallerCombinedVariables<Path, ParamsSchema>,
    context: BaseRequestCompletionContext
  ) => void;
}

export function createMutationBaseMixin<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  TransformedResult extends object = z.infer<ResponseSchema>,
>(
  definitionOptions: MutationDefinitionOptions<
    Path,
    ParamsSchema,
    ResponseSchema,
    TransformedResult
  >
) {
  function makeFetcher(
    fetcherOptions: MutationFetcherOptions<Path, ParamsSchema, TransformedResult> = {}
  ) {
    const { onError, onSuccess } = mergeCallbackOptionsWithPick(
      ["onError", "onSuccess"],
      definitionOptions,
      fetcherOptions
    );

    return makeDefaultMutationFetcher({
      ...definitionOptions,
      ...fetcherOptions,
      headers: {
        ...definitionOptions.headers,
        ...fetcherOptions.headers,
      },
      getSignal: () => fetcherOptions.signal,
      onError,
      onSuccess,
    });
  }

  return {
    useMutation(
      callerOptions: UseMutationHookCallerOptions<Path, ParamsSchema, TransformedResult> = {}
    ) {
      return useMutation({
        mutationFn: makeFetcher(callerOptions),
        // we do not want to pass onSuccess and onError to the hook since
        // the fetcher will call them for us
        ...omitKeys(mergeUseMutationOptions(definitionOptions, callerOptions), [
          "onSuccess",
          "onError",
        ]),
      });
    },
    async trigger(callerOptions: MutationTriggerOptions<Path, ParamsSchema, TransformedResult>) {
      const fetcher = makeFetcher(callerOptions);

      return fetcher(callerOptions);
    },
  };
}

// we allow passing options when creating the hook and when calling the hook.
// this merges them in a "smart" way to handle callbacks correctly by calling
// all callbacks when the same callback is passed in both places instead of
// overwriting them. bad types are fine here it'd be super hard to get right
// otherwise.
function mergeUseMutationOptions<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  TransformedResult,
>(...optionsList: UseMutationHookCallerOptions<Path, ParamsSchema, TransformedResult>[]) {
  return mergeCallbackOptions(["onSettled"], ...optionsList);
}
